import { Form, InputNumber, Radio, Typography } from "antd";
import { useEffect, useMemo } from "react";
import styled from "styled-components";
import { FluxoResposta } from "../cadastroCotaUsuario.types";
import { OnboardingFrame, SubmitButton } from "../utils";

interface Props {
  fluxo: FluxoResposta;
  submitting: boolean;
  onConcluir: (payload: Record<string, unknown>) => Promise<void>;
}

const Opcoes = styled(Radio.Group)`
  display: grid;
  gap: 14px;
  width: 100%;
  .ant-radio-wrapper {
    align-items: flex-start;
    border: 1px solid #d7e2e6;
    border-radius: 14px;
    margin: 0;
    padding: 18px 20px;
  }
  .ant-radio-wrapper-checked { background: #eef8f8; border-color: #0b6673; }
`;

const formatarMoeda = (valor: number) =>
  valor.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

export function EtapaSaldoDevedor({ fluxo, submitting, onConcluir }: Props) {
  const [form] = Form.useForm();
  const venda = fluxo.dadosPreenchidos?.venda as Record<string, unknown> | undefined;
  const dadosAntigos = fluxo.dadosPreenchidos?.saldoDevedor as Record<string, unknown> | undefined;
  const quantidadeParcelas = venda?.quantidadeParcelasDevedoras ?? dadosAntigos?.parcelasRestantes;
  const possuiParcelas = quantidadeParcelas !== null && quantidadeParcelas !== undefined && quantidadeParcelas !== "";
  const salvo = useMemo<Record<string, unknown> | undefined>(() => (
    venda || dadosAntigos
      ? {
          modo: dadosAntigos?.modo ?? (possuiParcelas ? "PARCELAS_RESTANTES" : "VALOR_TOTAL"),
          saldoDevedor: venda?.saldoDevedor ?? dadosAntigos?.saldoDevedor,
          parcelasRestantes: quantidadeParcelas,
          valorParcela: venda?.valorParcela ?? dadosAntigos?.valorParcela,
        }
      : undefined
  ), [venda, dadosAntigos, possuiParcelas, quantidadeParcelas]);

  useEffect(() => { form.setFieldsValue(salvo); }, [form, salvo]);

  function concluir(values: Record<string, unknown>) {
    if (values.modo === "VALOR_TOTAL") {
      return onConcluir({
        modo: "VALOR_TOTAL",
        saldoDevedor: values.saldoDevedor,
      });
    }

    return onConcluir({
      modo: "PARCELAS_RESTANTES",
      parcelasRestantes: values.parcelasRestantes,
      valorParcela: values.valorParcela,
    });
  }

  return (
    <OnboardingFrame
      fluxo={fluxo}
      title="Qual é o saldo devedor da sua cota?"
      description="Você pode informar o saldo devedor total ou, se não souber, informar o valor e a quantidade das parcelas restantes."
    >
      <Form layout="vertical" form={form} initialValues={salvo} onFinish={concluir}>
        <Form.Item name="modo" label="Como prefere informar?" rules={[{ required: true, message: "Escolha uma opção para continuar." }]}>
          <Opcoes>
            <Radio value="VALOR_TOTAL"><Typography.Text strong>Sei o saldo devedor total</Typography.Text></Radio>
            <Radio value="PARCELAS_RESTANTES"><Typography.Text strong>Sei o valor e a quantidade de parcelas restantes</Typography.Text></Radio>
          </Opcoes>
        </Form.Item>

        <Form.Item noStyle shouldUpdate={(prev, next) => prev.modo !== next.modo || prev.valorParcela !== next.valorParcela || prev.parcelasRestantes !== next.parcelasRestantes}>
          {({ getFieldValue }) => {
            const modo = getFieldValue("modo");
            const valorParcela = Number(getFieldValue("valorParcela") || 0);
            const parcelas = Number(getFieldValue("parcelasRestantes") || 0);
            const estimativa = valorParcela * parcelas;
            return modo === "VALOR_TOTAL" ? (
              <Form.Item name="saldoDevedor" label="Saldo devedor atual" rules={[{ required: true, message: "Informe o saldo devedor." }]}>
                <InputNumber prefix="R$" min={0} style={{ width: "100%" }} decimalSeparator="," placeholder="0,00" />
              </Form.Item>
            ) : modo === "PARCELAS_RESTANTES" ? (
              <>
                <Form.Item name="valorParcela" label="Valor atual da parcela" rules={[{ required: true, message: "Informe o valor da parcela." }]}>
                  <InputNumber prefix="R$" min={0} style={{ width: "100%" }} decimalSeparator="," placeholder="0,00" />
                </Form.Item>
                <Form.Item name="parcelasRestantes" label="Quantas parcelas ainda faltam?" rules={[{ required: true, message: "Informe a quantidade de parcelas." }]}>
                  <InputNumber min={1} precision={0} addonAfter="parcelas" style={{ width: "100%" }} placeholder="0" />
                </Form.Item>
                {valorParcela > 0 && parcelas > 0 ? (
                  <Typography.Paragraph>
                    <Typography.Text strong>Valor restante estimado: {formatarMoeda(estimativa)}</Typography.Text><br />
                    <Typography.Text type="secondary">Calculado com base em {parcelas} parcelas de {formatarMoeda(valorParcela)}.</Typography.Text>
                  </Typography.Paragraph>
                ) : null}
              </>
            ) : null;
          }}
        </Form.Item>
        <SubmitButton label="Continuar" loading={submitting} />
      </Form>
    </OnboardingFrame>
  );
}
